import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { PublicOnlyRoute } from './auth/PublicOnlyRoute';
import PublicLayout from './layouts/PublicLayout';
import AppLayout from './layouts/AppLayout';
import ScrollToTop from './components/common/ScrollToTop';
import PageViewTracker from './components/common/PageViewTracker';
import CookieConsent from './components/common/CookieConsent';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ToastProvider } from './components/ui/Toast';
import RootRedirect from './components/RootRedirect';
import ExternalRedirect from './components/common/ExternalRedirect';
import { PRIVACY_URL, TERMS_URL } from './config/legal';
import ProductDisplayPage from './pages/public/ProductDisplayPage';
import { ROUTER_FUTURE } from './routerFuture';

// Lazy load pages
const LoginForm = lazy(() => import('./components/auth/LoginForm'));
const RegisterForm = lazy(() => import('./components/auth/RegisterForm'));
const VerifyEmailForm = lazy(() => import('./components/auth/VerifyEmailForm'));
const ForgotPasswordForm = lazy(() => import('./components/auth/ForgotPasswordForm'));
const ResetPasswordForm = lazy(() => import('./components/auth/ResetPasswordForm'));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'));
const VaultGate = lazy(() => import('./pages/vault/VaultGate'));
const SharedCatalogPage = lazy(() => import('./pages/public/SharedCatalogPage'));
const ConnectPage = lazy(() => import('./pages/oauth/ConnectPage'));

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <ToastProvider />
        <Router future={ROUTER_FUTURE}>
          <ScrollToTop />
          <PageViewTracker />
          {/* Analytics consent (privacy decision 2026-10-31): PostHog waits for it */}
          <CookieConsent />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Root redirect — authenticated → /workspace, unauthenticated → /login */}
              <Route path="/" element={<RootRedirect />} />

              {/* Product Display Routes (Public - No Layout for full-screen experience) */}
              <Route path="/product/:id" element={<ProductDisplayPage />} />
              <Route path="/p/:id" element={<ProductDisplayPage />} />

              {/* The guest-code "try" flow is gone (#395). Old links (CV QR codes,
                  basiqr.com/try/<code> via the landing CloudFront function) land
                  on the login page instead of a dead page. */}
              <Route path="/try" element={<Navigate to="/login" replace />} />
              <Route path="/try/:code" element={<Navigate to="/login" replace />} />

              {/* Consumer Vault (standalone, full-screen, no PublicLayout mobile gate).
                  Its own guard: signed-out shoppers get the vault's phone-friendly
                  sign-in, not the brand /login page (#395). */}
              <Route path="/vault" element={<VaultGate />} />

              {/* The legal texts live on the landing site; these paths only
                  forward there, so links already sent out keep working. */}
              <Route path="/terms" element={<ExternalRedirect to={TERMS_URL} />} />
              <Route path="/privacy" element={<ExternalRedirect to={PRIVACY_URL} />} />
              {/* Share Links receiver page (standalone, no PublicLayout) */}
              <Route path="/s/:id" element={<SharedCatalogPage />} />

              {/* OAuth consent for MCP clients such as Claude (#272). Standalone:
                  no PublicLayout mobile gate (Claude mobile must work) and no
                  ProtectedRoute bounces — the page handles sign-in itself. */}
              <Route path="/connect" element={<ConnectPage />} />

              {/* Public Routes */}
              <Route element={<PublicLayout />}>
                {/* Signed-in users are bounced to their account's landing view:
                    Amplify v6 throws UserAlreadyAuthenticatedException rather than
                    signing in over a live session (#261). */}
                <Route path="/login" element={<PublicOnlyRoute><LoginForm /></PublicOnlyRoute>} />
                <Route path="/register" element={<PublicOnlyRoute><RegisterForm /></PublicOnlyRoute>} />
                <Route path="/verify-email" element={<VerifyEmailForm />} />
                <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordForm /></PublicOnlyRoute>} />
                <Route path="/reset-password" element={<ResetPasswordForm />} />
                <Route path="/set-password" element={<Navigate to="/login" replace />} />
              </Route>

              {/* Protected Routes */}
              <Route path="/workspace" element={
                <ProtectedRoute>
                  <WorkspacePage />
                </ProtectedRoute>
              } />

              <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<Navigate to="/workspace?view=dashboard" replace />} />
                <Route path="/create-product" element={<Navigate to="/workspace?view=create-product" replace />} />
                <Route path="/create-item" element={<Navigate to="/workspace?view=create-item" replace />} />
                <Route path="/create-collection" element={<Navigate to="/workspace?view=create-collection" replace />} />

                {/* Admin & Team Routes — redirect to workspace views */}
                <Route path="/admin" element={<Navigate to="/workspace?view=team" replace />} />
                <Route path="/sub-users" element={<Navigate to="/workspace?view=team" replace />} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
